import { TypedEventEmitter } from "../connect/typedEventEmitter";
import { IParam, MxObject, TMapLookup } from "../interfaces";
import * as MendixApi from "../mendixApi";

export class NonPersistentEntityHandler {
    /**
     * Data is Id of updated Entity
     */
    private _onUpdated = new TypedEventEmitter<string>();
    public onEntityUpdated = this._onUpdated.listenerAppender;
    /**
     * Data is Id of deleted Entity
     */
    private _onDeleted = new TypedEventEmitter<string>();
    public onEntityDeleted = this._onDeleted.listenerAppender;
    /**
     * Data is New Mendix Object
     */
    private _onEntityCreated = new TypedEventEmitter<MxObject>();
    public onEntityCreated = this._onEntityCreated.listenerAppender;

    private allEmitters: Array<TypedEventEmitter<any>> = [
        this._onUpdated, this._onDeleted, this._onEntityCreated,
    ];
    protected _entityLookup: TMapLookup<MxObject> = new Map();
    protected _subcribeHandlers: TMapLookup<any> = new Map();

    // _entity: mendix entity identifier
    constructor(protected _entity: string) { }
    public async createThenCommitEntity(paramList: IParam[]): Promise<MxObject | undefined> {
        let newEntity = await MendixApi.createNewObject(this._entity);
        if (newEntity) {
            const updatedEntity = await this.updateAndCommitEntity(newEntity, paramList);
            if (updatedEntity) { newEntity = updatedEntity; }
        } else {
            MendixApi.throwError("Failed to create");
        }
        newEntity && this.subscribeEntity(newEntity.getGuid());
        return newEntity;
    }
    public async createByMfThenCommitEntity(contextId: string, createMf: string, paramList: IParam[]) {
        const newEntities = await MendixApi.callMicroflow(createMf, contextId);
        let newEntity = newEntities ? newEntities[0] as MxObject : undefined;
        if (newEntity) {
            const updatedEntity = await this.updateAndCommitEntity(newEntity, paramList);
            if (updatedEntity) { newEntity = updatedEntity; }
        } else {
            MendixApi.throwError("Failed to create");
        }
        newEntity && this.subscribeEntity(newEntity.getGuid());
        return newEntity;
    }
    public async updateAndCommitEntity(entity: MxObject, paramList: IParam[]): Promise<MxObject | undefined> {
        const updatedEntity = await MendixApi.commitObject(entity, paramList);
        if (updatedEntity) {// if success
            this.updateLookupMap(updatedEntity);
            return updatedEntity;
        } else {
            MendixApi.throwError("Failed to commit");
            return undefined;
        }
    }
    public async callMicroflowWithEntity(microflow: string, guid: string, showProgress: boolean = false): Promise<any> {
        return MendixApi.callMicroflow(microflow, guid, showProgress);
    }
    public async callNanoflowWithEntity(
        nanoflow: mx.Nanoflow, entityType: string, entity: MxObject, showProgress: boolean = false
    ): Promise<any> {
        return MendixApi.callNanoflow(nanoflow, entityType, entity, showProgress);
    }
    /** Destroy */
    public async destroy() {
        await this.deleteAllEntites();
        this.allEmitters.forEach(emitter => emitter.off());
        delete this.allEmitters;
        this.unsubscribeAll();
        this._entityLookup && this._entityLookup.clear();
        delete this._entityLookup;
    }
    public async updateEntity(guid: string): Promise<string | undefined> {
        const entity = await MendixApi.retrieveObjectById(guid);
        if (entity && !(entity as any).isNew()) {
            this.updateLookupMap(entity);
        } else if (entity && (entity as any).isNew()) {
            return undefined;
        } else {
            /**
             * Here we delete the entity but not unsubscribe to listen to its change
             * Reason: if we create a new object then open the new/edit form. In the form, if we refresh the object.
             * Then we cannot retrieve the object by the previous retrieve.
             * So we will delete the enty by guid (the next command).
             * If we unsubscribe, in the delete, if we commit the object by click save,
             * then we cannot get the signal that the object
             * is changed in order to refresh it in the canvas. So we keep the subscribe here.
             * The subscription will be remove anyway when uninitialize the widget.
             */
            const oldEntity = this._entityLookup.get(guid);
            this.deleteEntity(oldEntity!, false);
        }
        return guid;
    }
    protected updateLookupMapFromArray(entities: MxObject[]) {
        entities.forEach((entity: MxObject) => {
            this.updateLookupMap(entity);
        });
    }
    protected updateLookupMap(entity: MxObject) {
        const guid: string = entity.getGuid();
        if (this._entityLookup.get(guid)) {
            this.updateExistingEntity(entity, guid);
        } else {
            this.addNewEntity(entity);
        }
    }
    protected addNewEntity(newEntity: MxObject) {
        this._entityLookup.set(newEntity.getGuid(), newEntity);
        this._onEntityCreated.emit(newEntity);
    }
    protected updateExistingEntity(newEntity: MxObject, guid: string) {
        const oldEntity = this._entityLookup.get(guid);
        if (!oldEntity) { }
        this._entityLookup.set(guid, newEntity);
        this._onUpdated.emit(newEntity.getGuid());
    }
    public async deleteAllEntites(willSubscribe: boolean = true) {
        const entities = this.getEntities();
        const promises = entities.map(entity => this.deleteEntity(entity, willSubscribe));
        await Promise.all(promises);
        this._entityLookup && this._entityLookup.clear();
    }
    public async deleteEntity(entity: MxObject, willSubscribe: boolean = true) {
        const entityId = entity.getGuid();
        await MendixApi.deleteObjectById(entityId);
        this._entityLookup && this._entityLookup.delete(entityId);
        if (willSubscribe) {
            this.unsubscribeEntity(entityId);
        }
        this._onDeleted.emit(entityId);
    }
    public getEntityMap(): TMapLookup<MxObject> {
        return this._entityLookup;
    }
    public getEntities() {
        return Array.from(this._entityLookup.values());
    }
    public has(id: string): boolean {
        return this._entityLookup.has(id);
    }
    public getEntity(id: string) {
        return this._entityLookup.get(id);
    }
    public getFirstEntity(): MxObject | undefined {
        return Array.from(this._entityLookup.values())[0];
    }
    protected subscribeEntities(entities: MxObject[]) {
        entities.forEach((entity: MxObject) => {
            this.subscribeEntity(entity.getGuid());
        });
    }
    public subscribeEntity(entityId: string) {
        if (this._subcribeHandlers.get(entityId) === undefined) {
            this._subcribeHandlers.set(
                entityId, MendixApi.subscribe(entityId, () => this.onEntityChanged(entityId))
            );
        }
    }
    /**
     * Implement this function in concreted class to handle entity changes
     * @param guid Id of Entity just changed
     */
    protected async onEntityChanged(guid: string): Promise<any> {
        return this.updateEntity(guid);
    }
    protected unsubscribeAll() {
        this._subcribeHandlers.forEach((_handler, guid) => this.unsubscribeEntity(guid));
        this._subcribeHandlers.clear();
    }
    public unsubscribeEntity(guid: string): void {
        const handler = this._subcribeHandlers.get(guid);
        if (handler) {
            MendixApi.unsubscribe(handler);
            this._subcribeHandlers.delete(guid);
        }
    }
}
